import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FaApple, FaWindows } from 'react-icons/fa';
import { ROUTES } from './siteNav';

// The year the macOS build is due. Shown on the card the macOS button opens.
const MAC_RELEASE_YEAR = '2027';

const BUTTON =
  'flex w-full items-center gap-3 rounded-[6px] border border-black bg-white px-4 py-2.5 text-left text-[15px] font-light text-black transition-colors hover:bg-[#EFEFEF] md:text-base';

/**
 * The brushed-metal card announcing the macOS build. Portalled to the body so
 * it sits over the whole page wherever the buttons happen to live — inside the
 * sticky header's dropdown as much as in the homepage cards.
 */
const MacComingCard = ({ onClose }) => {
  useEffect(() => {
    // Capture phase, and stopped there: the header closes its open panel on
    // Escape too, and one press should only dismiss the card on top.
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      // The header's outside-click handler listens for mousedown on the
      // document; without this, dismissing the card would close the nav panel
      // underneath it as well.
      onMouseDown={(event) => event.stopPropagation()}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 p-4"
    >
      {/* The supplied brushed-metal plate (10-1 round) is the card itself;
          the wordmark and year are set over it, with the Apple mark that is
          already in the picture between them. Clicks inside stay inside. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`No / SCAM for macOS arrives in ${MAC_RELEASE_YEAR}`}
        onClick={(event) => event.stopPropagation()}
        className="relative aspect-[232/174] w-full max-w-[360px] rounded-[22px] bg-cover bg-center text-black shadow-[0_18px_40px_rgba(0,0,0,0.35)]"
        style={{ backgroundImage: "url('/images/site/nav/mac-2027.jpeg')" }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-[22px] leading-none text-black/70 transition-colors hover:bg-black/10 hover:text-black"
        >
          &times;
        </button>
        {/* pointer-events-none so the full-width lines never sit over the
            close button. */}
        <p className="pointer-events-none absolute inset-x-0 top-[9%] text-center text-[26px] font-bold leading-none">
          No / SCAM
        </p>
        <p className="pointer-events-none absolute inset-x-0 bottom-[8%] text-center text-[24px] font-light leading-none">
          {MAC_RELEASE_YEAR}
        </p>
      </div>
    </div>,
    document.body
  );
};

/**
 * The pair of "Get No / SCAM" buttons (10-1 round): Windows goes to checkout,
 * macOS is not out yet and opens the release card instead.
 */
const GetNoScamButtons = ({ className = '' }) => {
  const [macOpen, setMacOpen] = useState(false);

  return (
    <div className={className}>
      <Link to={ROUTES.getStarted} className={BUTTON}>
        <FaWindows aria-hidden="true" className="h-6 w-6 flex-shrink-0" />
        <span className="flex-1 text-center">No / SCAM for Windows OS</span>
      </Link>
      <button
        type="button"
        onClick={() => setMacOpen(true)}
        aria-haspopup="dialog"
        className={BUTTON}
      >
        <FaApple aria-hidden="true" className="h-6 w-6 flex-shrink-0" />
        <span className="flex-1 text-center">No / SCAM for macOS</span>
      </button>
      {macOpen && <MacComingCard onClose={() => setMacOpen(false)} />}
    </div>
  );
};

export default GetNoScamButtons;
